import * as React from 'react';
import { observer } from 'mobx-react';
import { observable, action, computed, makeObservable } from 'mobx';
import classNames from 'classnames';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { Button, Display } from '../Button/Button';

export interface ITabConfig {
	name: React.ReactNode;
	component: React.ReactNode;
	key: string;
	className?: string;
	disabled?: boolean;
}
export interface ITabsProps {
	tabs: Array<ITabConfig>;
	leftTab?: React.ReactNode;
	rightTab?: React.ReactNode;
	className?: string;
	innerProps?: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
	defaultTab?: number;
	currentTab?: number;
	onTabClicked?: (tabIndex: number, event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	/**
	 * When true, horizontally centres the selected tab within its (scrollable) header row on mount
	 * and whenever the selected tab changes. Used by the date calendar so the selected day is
	 * visible on load instead of scrolled off-screen.
	 */
	scrollSelectedIntoView?: boolean;
	/**
	 * When true, shows hovering left/right scroll arrows over the header row whenever there are
	 * tabs scrolled off either edge, so it's clear there's more to see.
	 */
	showScrollArrows?: boolean;
}
interface ITabHeaderProps {
	tabState: { currentTab: number };
	tabIdx: number;
	tabChanged?: () => void;
	onTabClicked?: (tabIndex: number, event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	children: React.ReactNode;
	disabled?: boolean;
}

class TabHeader extends React.Component<ITabHeaderProps> {
	constructor(props: ITabHeaderProps) {
		super(props);

		makeObservable<TabHeader, 'onTabClicked'>(this, {
			onTabClicked: action,
		});
	}

	public render() {
		const { children, disabled } = this.props;
		return (
			<Button display={Display.Text} onClick={this.onTabClicked} disabled={disabled}>
				{children}
			</Button>
		);
	}

	private onTabClicked = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		if (this.props.tabState.currentTab !== this.props.tabIdx && this.props.tabChanged) {
			this.props.tabChanged();
		}
		this.props.tabState.currentTab = this.props.tabIdx;
		if (this.props.onTabClicked) {
			this.props.onTabClicked(this.props.tabIdx, event);
		}
	};
}

class Tabs extends React.Component<ITabsProps> {
	public tabState = {
		currentTab: 0,
	};

	// Whether there are tabs scrolled off the left/right edge — drives the hovering scroll arrows.
	public scrollAffordance = {
		canScrollLeft: false,
		canScrollRight: false,
	};

	public get currentTab() {
		const { currentTab } = this.props;
		return currentTab ?? this.tabState.currentTab;
	}

	constructor(props: ITabsProps) {
		super(props);

		makeObservable(this, {
			tabState: observable,
			scrollAffordance: observable,
			currentTab: computed,
		});

		const { defaultTab } = this.props;

		if (defaultTab) {
			this.tabState.currentTab = defaultTab;
		}
	}

	private ulRef = React.createRef<HTMLUListElement>();

	private lastCentredTab = -1;

	private scrollCheckTimeout?: ReturnType<typeof setTimeout>;

	public componentDidMount() {
		if (this.props.scrollSelectedIntoView) {
			// Centre instantly on load so the selected tab is already in view (no visible scroll).
			this.centreSelectedTab('auto');
		}
		if (this.props.showScrollArrows) {
			this.ulRef.current?.addEventListener('scroll', this.handleScroll);
			window.addEventListener('resize', this.updateScrollAffordance);
			this.updateScrollAffordance();
		}
	}

	public componentDidUpdate() {
		if (this.props.scrollSelectedIntoView && this.currentTab !== this.lastCentredTab) {
			this.centreSelectedTab('smooth');
		}
		if (this.props.showScrollArrows) {
			// Tab content/selection may have changed the scrollable extent.
			this.updateScrollAffordance();
		}
	}

	public componentWillUnmount() {
		this.ulRef.current?.removeEventListener('scroll', this.handleScroll);
		window.removeEventListener('resize', this.updateScrollAffordance);
		if (this.scrollCheckTimeout) {
			clearTimeout(this.scrollCheckTimeout);
		}
	}

	private handleScroll = () => {
		if (this.scrollCheckTimeout) {
			clearTimeout(this.scrollCheckTimeout);
		}
		this.scrollCheckTimeout = setTimeout(this.updateScrollAffordance, 50);
	};

	private updateScrollAffordance = action(() => {
		const ul = this.ulRef.current;
		if (!ul) {
			return;
		}
		this.scrollAffordance.canScrollLeft = ul.scrollLeft > 0;
		// 2px buffer for sub-pixel rounding.
		this.scrollAffordance.canScrollRight = ul.scrollWidth > ul.clientWidth + ul.scrollLeft + 2;
	});

	private scrollByPage = (direction: -1 | 1) => {
		const ul = this.ulRef.current;
		if (!ul) {
			return;
		}
		// Scroll by most of the visible width so a fresh batch of tabs comes into view.
		ul.scrollBy({ left: direction * ul.clientWidth * 0.8, behavior: 'smooth' });
	};

	private centreSelectedTab(behavior: ScrollBehavior) {
		const ul = this.ulRef.current;
		if (!ul) {
			return;
		}
		const selected = ul.querySelector('li.tab-header.selected') as HTMLElement | null;
		if (!selected) {
			return;
		}
		// Centre the selected tab horizontally within the scroll container. We adjust scrollLeft
		// directly rather than using scrollIntoView, which would also scroll the page vertically.
		const childOffset = selected.getBoundingClientRect().left - ul.getBoundingClientRect().left + ul.scrollLeft;
		const target = childOffset - (ul.clientWidth - selected.offsetWidth) / 2;
		ul.scrollTo({ left: Math.max(0, target), behavior });
		this.lastCentredTab = this.currentTab;
	}

	public render() {
		const { innerProps, tabs, leftTab, rightTab, className, onTabClicked, showScrollArrows } = this.props;

		return (
			<div className={classNames('tabs__container', className)}>
				<nav {...innerProps} className={classNames('tabs', className)}>
					{showScrollArrows && this.scrollAffordance.canScrollLeft && (
						<button
							type="button"
							className="tabs__scroll-arrow tabs__scroll-arrow--left"
							aria-label="Scroll left"
							onClick={() => this.scrollByPage(-1)}
						>
							<span className="tabs__scroll-arrow__circle">
								<Icon name="chevron_left" useMaterialIcons />
							</span>
						</button>
					)}
					<ul ref={this.ulRef}>
						{leftTab}
						{tabs.map((tab, idx) => {
							return (
								<li
									key={tab.key}
									className={classNames(
										'tab-header',
										this.currentTab === idx ? ' selected' : '',
										tab.disabled ? ' disabled' : '',
									)}
								>
									<TabHeader
										tabState={this.tabState}
										tabIdx={idx}
										onTabClicked={onTabClicked}
										disabled={tab.disabled}
									>
										{tab.name}
									</TabHeader>
								</li>
							);
						})}
						{rightTab}
					</ul>
					{showScrollArrows && this.scrollAffordance.canScrollRight && (
						<button
							type="button"
							className="tabs__scroll-arrow tabs__scroll-arrow--right"
							aria-label="Scroll right"
							onClick={() => this.scrollByPage(1)}
						>
							<span className="tabs__scroll-arrow__circle">
								<Icon name="chevron_right" useMaterialIcons />
							</span>
						</button>
					)}
				</nav>
				<div className={classNames(tabs[this.currentTab]?.className, 'tab-body-section')}>
					{tabs[this.currentTab]?.component}
				</div>
			</div>
		);
	}
}
export default observer(Tabs);
